import { useRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { assetUrl } from '../lib/config'

/**
 * Backdrop-Klick, der nur schließt, wenn der Klick auch AUF dem Backdrop
 * begonnen hat. Hintergrund: Endet ein Drag (z. B. Text im Titelfeld markieren)
 * außerhalb des Dialog-Panels, feuert der Browser den Click auf dem gemeinsamen
 * Vorfahren — dem Backdrop — und der Dialog ging kommentarlos zu (auf WebKit/
 * macOS-App im Kalender reproduziert). Daher: Schließen nur, wenn pointerdown
 * UND click direkt auf dem Backdrop landen.
 */
export function useBackdropClose(onClose: () => void): {
  onPointerDown: React.PointerEventHandler<HTMLDivElement>
  onClick: React.MouseEventHandler<HTMLDivElement>
} {
  const armed = useRef(false)
  return {
    onPointerDown: (e) => {
      armed.current = e.target === e.currentTarget
    },
    onClick: (e) => {
      const hit = armed.current && e.target === e.currentTarget
      armed.current = false
      if (hit) onClose()
    },
  }
}

/** Initialen aus einem Namen (max. 2). */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

/** Avatar mit Personenfarben-Ring (immer + Name kombiniert, 04 – NFR).
    Zeigt das Profilbild, wenn `src` gesetzt ist, sonst die Initialen. */
export function Avatar({
  name,
  color,
  size = 32,
  src,
}: {
  name: string
  color: string
  size?: number
  src?: string | null
}): React.JSX.Element {
  // Feiner Personenfarben-Ring: schmale helle Trennung + dünner Farbring,
  // plus zarte Innenkante, damit das Foto sauber gegen helle Flächen absetzt.
  const ring = {
    boxShadow: `0 0 0 1.5px var(--color-card), 0 0 0 3px ${color}, inset 0 0 0 1px rgba(0,0,0,0.06)`,
  }
  if (src) {
    return (
      <img
        src={assetUrl(src)}
        alt={name}
        title={name}
        width={size}
        height={size}
        className="block shrink-0 rounded-full object-cover object-center"
        style={{ width: size, height: size, backgroundColor: color, ...ring }}
      />
    )
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, backgroundColor: color, fontSize: size * 0.4, ...ring }}
      title={name}
      aria-hidden
    >
      {initials(name)}
    </span>
  )
}

/**
 * Kompakte Fehleranzeige mit „Erneut versuchen" (Mittel): ersetzt leere Bildschirme
 * bei API-Fehlern/Offline. Screens setzen bei fehlgeschlagenem Laden einen Fehler
 * und rendern diesen Banner statt still nichts zu zeigen.
 */
export function ErrorBanner({ message = 'Konnte nicht geladen werden.', onRetry }: { message?: string; onRetry?: () => void }): React.JSX.Element {
  return (
    <div
      role="alert"
      className="mx-auto my-4 flex max-w-md items-center gap-3 rounded-[var(--radius-card)] border p-3 text-sm"
      style={{ borderColor: 'var(--color-danger)', backgroundColor: 'color-mix(in srgb, var(--color-danger) 8%, transparent)', color: 'var(--color-ink)' }}
    >
      <span className="flex-1">{message}</span>
      {onRetry && (
        <button onClick={onRetry} className="min-h-11 rounded-lg px-3 py-1 text-sm font-medium text-white" style={{ backgroundColor: 'var(--color-danger)' }}>
          Erneut versuchen
        </button>
      )}
    </div>
  )
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger'
  children: ReactNode
}

export function Button({ variant = 'primary', className = '', ...rest }: ButtonProps): React.JSX.Element {
  // min-h-11 (44px) = barrierearmes Touch-Ziel (Mittel), gut für Kinder/Wand-Tablet.
  const base =
    'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
  const styles: Record<string, string> = {
    primary: 'text-white hover:brightness-110',
    ghost: 'hover:bg-black/5',
    danger: 'text-white hover:brightness-110',
  }
  const bg =
    variant === 'primary'
      ? { backgroundColor: 'var(--color-accent)' }
      : variant === 'danger'
        ? { backgroundColor: 'var(--color-danger)' }
        : { color: 'var(--color-ink)' }
  return <button className={`${base} ${styles[variant]} ${className}`} style={bg} {...rest} />
}
